import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
  FaUserCircle, 
  FaFileAlt, 
  FaCheckCircle, 
  FaCalendarAlt,
  FaArrowLeft,
  FaArrowRight,
  FaCloudUploadAlt
} from 'react-icons/fa';
import './ProfileResumeSelector.css';
import Loader from '../common/Loader';
import ResumeUploadCard from './ResumeUploadCard';

const ProfileResumeSelector = ({ 
  profiles, 
  resumes, 
  selectedProfileId, 
  selectedResumeId,
  onSelectionChange,
  onPrev,
  onNext,
  isLoading,
  refreshProfiles
}) => {
  const [activeTab, setActiveTab] = useState('profiles');
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  // Handle profile selection
  const handleProfileSelect = (profileId) => {
    onSelectionChange('selectedProfileId', profileId);
    
    // Clear resume selection if profile is selected
    if (selectedResumeId) {
      onSelectionChange('selectedResumeId', null);
    }
  };
  
  // Handle resume selection
  const handleResumeSelect = (resumeId) => {
    onSelectionChange('selectedResumeId', resumeId);
    
    // Clear profile selection if resume is selected
    if (selectedProfileId) {
      onSelectionChange('selectedProfileId', null);
    }
  };
  
  // Handle upload completion
  const handleUploadComplete = () => {
    // Refresh the profiles and resumes data
    if (refreshProfiles) {
      refreshProfiles();
    }
    
    // Switch to resumes tab to show the newly uploaded resume
    setActiveTab('resumes');
  };
  
  // Check if next button should be enabled
  const isNextEnabled = () => {
    return selectedProfileId || selectedResumeId;
  };
  
  if (isLoading) {
    return <Loader text="Loading your profiles and resumes..." />;
  }
  
  return (
    <div className="profile-resume-selector">
      <div className="section-description">
        <h2>Select Source for Tailoring</h2>
        <p>
          Choose a profile or resume to use as the basis for your tailored resume.
          We'll optimize it to match the job description you provided.
        </p>
      </div>
      
      <div className="selector-tabs">
        <button
          className={`tab-button ${activeTab === 'profiles' ? 'active' : ''}`}
          onClick={() => setActiveTab('profiles')}
        >
          <FaUserCircle className="tab-icon" /> Profiles
        </button>
        <button
          className={`tab-button ${activeTab === 'resumes' ? 'active' : ''}`}
          onClick={() => setActiveTab('resumes')}
        >
          <FaFileAlt className="tab-icon" /> Resumes
        </button>
        <button
          className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          <FaCloudUploadAlt className="tab-icon" /> Upload New
        </button>
      </div>
      
      <div className="selector-content">
        {activeTab === 'profiles' && (
          <div className="selection-grid">
            {profiles.length > 0 ? (
              profiles.map(profile => (
                <div 
                  key={profile.id}
                  className={`selection-card ${selectedProfileId === profile.id ? 'selected' : ''}`}
                  onClick={() => handleProfileSelect(profile.id)}
                >
                  <div className="card-header">
                    <div className="card-icon">
                      <FaUserCircle />
                    </div>
                    <div className="card-title">
                      <h3>{profile.name}</h3>
                      {profile.is_primary && (
                        <span className="primary-badge">Primary</span>
                      )}
                    </div>
                    {selectedProfileId === profile.id && (
                      <div className="selected-indicator">
                        <FaCheckCircle />
                      </div>
                    )}
                  </div>
                  
                  <div className="card-content">
                    <div className="card-details">
                      <div className="detail-item">
                        <FaCalendarAlt className="detail-icon" />
                        <span>Created: {formatDate(profile.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <FaUserCircle className="empty-icon" />
                <p>You don't have any profiles yet.</p>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'resumes' && (
          <div className="selection-grid">
            {resumes.length > 0 ? (
              resumes.map(resume => (
                <div 
                  key={resume.id}
                  className={`selection-card ${selectedResumeId === resume.id ? 'selected' : ''}`}
                  onClick={() => handleResumeSelect(resume.id)}
                >
                  <div className="card-header">
                    <div className="card-icon">
                      <FaFileAlt />
                    </div>
                    <div className="card-title">
                      <h3>{resume.name}</h3>
                      {resume.is_primary && (
                        <span className="primary-badge">Primary</span>
                      )}
                    </div>
                    {selectedResumeId === resume.id && (
                      <div className="selected-indicator">
                        <FaCheckCircle />
                      </div>
                    )}
                  </div>
                  
                  <div className="card-content">
                    <div className="card-details">
                      <div className="detail-item">
                        <FaCalendarAlt className="detail-icon" />
                        <span>Created: {formatDate(resume.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <FaFileAlt className="empty-icon" />
                <p>You don't have any resumes yet.</p>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'upload' && (
          <div className="selection-grid">
            <ResumeUploadCard onUploadComplete={handleUploadComplete} />
          </div>
        )}
      </div>
      
      <div className="selection-info">
        <div className="info-card">
          <h4>What's the difference?</h4>
          <div className="info-content">
            <p>
              <strong>Profile:</strong> Your complete professional profile with all work experience, education, skills, and projects.
            </p>
            <p>
              <strong>Resume:</strong> A previously created resume that may already have some customizations for a specific position or industry.
            </p>
            <p>
              <strong>Upload New:</strong> Upload a resume file from your computer in PDF, DOC, DOCX, or TXT format.
            </p>
          </div>
        </div>
      </div>
      
      <div className="selector-actions">
        <button onClick={onPrev} className="btn btn-outline">
          <FaArrowLeft /> Job Description
        </button>
        <button 
          onClick={onNext} 
          className="btn btn-primary"
          disabled={!isNextEnabled()}
        >
          Custom Inputs <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

ProfileResumeSelector.propTypes = {
  profiles: PropTypes.array.isRequired,
  resumes: PropTypes.array.isRequired,
  selectedProfileId: PropTypes.number,
  selectedResumeId: PropTypes.number,
  onSelectionChange: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  refreshProfiles: PropTypes.func
};

export default ProfileResumeSelector;